import React, { useRef, memo } from 'react';
import ReactEChartsCore from '../EChart';
import * as echarts from 'echarts/core';
import { Card } from 'react-bootstrap';
import {
  TooltipComponent,
  GridComponent,
  LegendComponent
} from 'echarts/components';
import { BarChart } from 'echarts/charts';
import { CanvasRenderer } from 'echarts/renderers';
import { useAppContext } from '../../../providers/AppProvider';

echarts.use([
  TooltipComponent,
  GridComponent,
  LegendComponent,
  BarChart,
  CanvasRenderer
]);
const getChartOptions = (theme, getThemeColor, shiftData) => {
  const shifts = Object.keys(shiftData); // Schimburile (A, B, C) pe axa X
  // const machines = Array.from(
  //   new Set(
  //     shifts.flatMap(shift => Object.keys(shiftData[shift])) // Mașini unice
  //   )
  // );
  const machines = Array.from(
    new Set(
      shifts
        .map(shift => Object.keys(shiftData[shift])) // Map each shift to its machine keys
        .reduce((acc, val) => acc.concat(val), []) // Flatten the resulting arrays
    )
  );

  // Culorile pentru bare
  const colors = {
    totalCurrent: theme === 'dark' ? `hsl(0, 70%, 50%)` : `hsl(0, 50%, 60%)`, // Reddish
    // L120 tandem are 4 componente de curent, deci paleta trebuie sa treaca de 2
    components: [200, 45, 120, 280, 20, 170].map(hue =>
      theme === 'dark' ? `hsl(${hue}, 70%, 50%)` : `hsl(${hue}, 50%, 60%)`
    )
  };

  const series = [];

  machines.forEach(machine => {
    // Bara Total Current
    series.push({
      name: 'Total Current',
      type: 'bar',
      stack: machine, // Grupat pe mașină
      data: shifts.map(
        shift => shiftData[shift]?.[machine]?.total_current || 0
      ),
      itemStyle: {
        color: colors.totalCurrent // Culoare pentru total current
      },
      label: {
        show: true,
        position: 'top',
        formatter: () => machine, // Afișează numele mașinii deasupra barei
        fontSize: 9,
        rotate: 90, // vertical — incap toate masinile fara suprapunere
        align: 'left',
        verticalAlign: 'middle',
        color: getThemeColor('gray-800')
      },
      barMaxWidth: 8
    });

    // Componente individuale
    const components = Object.keys(
      shiftData[shifts[0]]?.[machine] || {}
    ).filter(key => key !== 'total_current');

    components.forEach((component, index) => {
      series.push({
        name: component,
        type: 'bar',
        stack: `${machine} Components`, // Grupat cu alte componente
        data: shifts.map(
          shift => shiftData[shift]?.[machine]?.[component] || 0
        ),
        itemStyle: {
          color: colors.components[index % colors.components.length] // Culoare diferită pentru fiecare componentă
        },
        label: {
          show: false // Fără etichete pentru componente
        },
        barMaxWidth: 8
      });
    });
  });

  return {
    tooltip: {
      trigger: 'item',
      formatter: ({ seriesName, value, name }) =>
        `<strong>${name}</strong><br>${seriesName}: ${value.toFixed(2)} amps`
    },
    legend: {
      data: [
        'Total Current',
        ...machines
          .map(machine =>
            Object.keys(shiftData[shifts[0]]?.[machine] || {}).filter(
              key => key !== 'total_current'
            )
          )
          .reduce((acc, val) => acc.concat(val), []) // Flatten the resulting arrays
      ],
      textStyle: {
        color: getThemeColor('gray-800'),
        fontSize: 12
      },
      bottom: 0
    },
    xAxis: {
      type: 'category',
      data: shifts, // Schimburile pe axa X
      axisLabel: {
        fontSize: 12,
        color: getThemeColor('gray-800')
      },
      axisLine: {
        lineStyle: {
          color: getThemeColor('gray-300')
        }
      },
      axisTick: {
        alignWithLabel: true
      }
    },
    yAxis: {
      type: 'value',
      axisLabel: {
        fontSize: 12,
        formatter: '{value} amps',
        color: getThemeColor('gray-800')
      },
      axisLine: {
        lineStyle: { color: getThemeColor('gray-300') }
      },
      splitLine: {
        show: true,
        lineStyle: { color: getThemeColor('gray-300') }
      }
    },
    series,
    grid: {
      left: '10%',
      right: '10%',
      bottom: '22%', // legenda creste cu componentele de curent ale fiecarei masini
      top: '15%', // loc pentru numele rotite deasupra barelor
      containLabel: true
    },
    animation: true
  };
};

const ShiftCurrentConsumedChart = ({ shiftData, title }) => {
  console.log('SHIFT CHARTS ', shiftData);
  const chartRef = useRef(null);
  const {
    config: { theme },
    getThemeColor
  } = useAppContext();

  return (
    <Card className="h-100">
      <Card.Body>
        <div style={styles.headerContainer}>
          <div style={styles.title}>{title}</div>
        </div>
        <ReactEChartsCore
          ref={chartRef}
          echarts={echarts}
          option={getChartOptions(theme, getThemeColor, shiftData)}
          style={{ height: '400px', width: '100%' }}
        />
      </Card.Body>
    </Card>
  );
};

const styles = {
  headerContainer: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: '10px'
  },
  title: {
    fontSize: '18px',
    fontWeight: 'bold'
  }
};

export default memo(ShiftCurrentConsumedChart);
